import { create } from 'zustand';
import { createSelectors } from '../utils/createSelectors';
import {
  CONSENT_STORAGE_KEY,
  DEFAULT_CONSENT_SETTINGS,
} from '../constants/analytics';
import {
  CONSENT_SOURCE,
  CONSENT_STATUS,
  type ConsentInitializationResult,
  type ConsentSettings,
} from '../types/analytics';

type ConsentStore = {
  consent: ConsentSettings;
  hasUserConsentChoice: boolean;
  initializeConsent: () => ConsentInitializationResult;
  updateConsent: (nextConsent: Partial<ConsentSettings>) => void;
  grantAllConsent: () => void;
  denyAllConsent: () => void;
  resetConsent: () => void;
};

const isBrowser = () => typeof window !== 'undefined';

const isConsentStatus = (value: unknown): value is 'granted' | 'denied' =>
  value === CONSENT_STATUS.GRANTED || value === CONSENT_STATUS.DENIED;

const parseStoredConsentSettings = (storedValue: string): ConsentSettings | null => {
  try {
    const parsedValue = JSON.parse(storedValue) as Partial<ConsentSettings>;

    if (
      !isConsentStatus(parsedValue.adStorage) ||
      !isConsentStatus(parsedValue.adUserData) ||
      !isConsentStatus(parsedValue.adPersonalization) ||
      !isConsentStatus(parsedValue.analyticsStorage) ||
      !isConsentStatus(parsedValue.functionalityStorage) ||
      !isConsentStatus(parsedValue.personalizationStorage) ||
      !isConsentStatus(parsedValue.securityStorage)
    ) {
      return null;
    }

    const waitForUpdate =
      typeof parsedValue.waitForUpdate === 'number'
        ? parsedValue.waitForUpdate
        : DEFAULT_CONSENT_SETTINGS.waitForUpdate;

    return {
      adStorage: parsedValue.adStorage,
      adUserData: parsedValue.adUserData,
      adPersonalization: parsedValue.adPersonalization,
      analyticsStorage: parsedValue.analyticsStorage,
      functionalityStorage: parsedValue.functionalityStorage,
      personalizationStorage: parsedValue.personalizationStorage,
      securityStorage: parsedValue.securityStorage,
      waitForUpdate,
    };
  } catch (error) {
    console.warn('Failed to parse stored consent settings:', error);
    return null;
  }
};

const saveConsentSettings = (consentSettings: ConsentSettings) => {
  if (!isBrowser()) {
    return;
  }

  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentSettings));
};

const loadConsentSettings = (): ConsentSettings | null => {
  if (!isBrowser()) {
    return null;
  }

  const storedValue = localStorage.getItem(CONSENT_STORAGE_KEY);
  if (!storedValue) {
    return null;
  }

  return parseStoredConsentSettings(storedValue);
};

const useConsentStoreBase = create<ConsentStore>((set, get) => ({
  consent: DEFAULT_CONSENT_SETTINGS,
  hasUserConsentChoice: false,

  initializeConsent: () => {
    const storedConsent = loadConsentSettings();

    if (storedConsent) {
      set({ consent: storedConsent, hasUserConsentChoice: true });
      return {
        consent: storedConsent,
        source: CONSENT_SOURCE.STORED,
      };
    }

    set({ consent: DEFAULT_CONSENT_SETTINGS, hasUserConsentChoice: false });
    return {
      consent: DEFAULT_CONSENT_SETTINGS,
      source: CONSENT_SOURCE.DEFAULT,
    };
  },

  updateConsent: (nextConsent) => {
    const previousConsent = get().consent;
    const updatedConsent: ConsentSettings = {
      ...previousConsent,
      ...nextConsent,
    };

    saveConsentSettings(updatedConsent);
    set({ consent: updatedConsent, hasUserConsentChoice: true });
  },

  grantAllConsent: () => {
    const grantedConsent: ConsentSettings = {
      ...DEFAULT_CONSENT_SETTINGS,
      adStorage: CONSENT_STATUS.GRANTED,
      adUserData: CONSENT_STATUS.GRANTED,
      adPersonalization: CONSENT_STATUS.GRANTED,
      analyticsStorage: CONSENT_STATUS.GRANTED,
      functionalityStorage: CONSENT_STATUS.GRANTED,
      personalizationStorage: CONSENT_STATUS.GRANTED,
      securityStorage: CONSENT_STATUS.GRANTED,
    };

    saveConsentSettings(grantedConsent);
    set({ consent: grantedConsent, hasUserConsentChoice: true });
  },

  denyAllConsent: () => {
    const deniedConsent: ConsentSettings = {
      ...DEFAULT_CONSENT_SETTINGS,
      adStorage: CONSENT_STATUS.DENIED,
      adUserData: CONSENT_STATUS.DENIED,
      adPersonalization: CONSENT_STATUS.DENIED,
      analyticsStorage: CONSENT_STATUS.DENIED,
      functionalityStorage: CONSENT_STATUS.DENIED,
      personalizationStorage: CONSENT_STATUS.DENIED,
      securityStorage: CONSENT_STATUS.GRANTED,
    };

    saveConsentSettings(deniedConsent);
    set({ consent: deniedConsent, hasUserConsentChoice: true });
  },

  resetConsent: () => {
    if (isBrowser()) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    }

    set({
      consent: DEFAULT_CONSENT_SETTINGS,
      hasUserConsentChoice: false,
    });
  },
}));

export const useConsentStore = createSelectors(useConsentStoreBase);
